import {Component, ElementRef, Inject, LOCALE_ID, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ProgressService} from '../../service/progress.service';
import {BaseModalComponent} from '../../common/modal/base-modal/base-modal.component';
import {EditorComponent} from '@tinymce/tinymce-angular';
import {EditorConfigHelper} from '../../helper/editor-config.helper';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {Answer, localizeQuestionType, Question, QuestionType} from '../../model/contest/ContestTask';
import {Role} from '../../model/auth/Role';
import {Observable, of} from 'rxjs';
import {MatSelectionListChange} from '@angular/material/list';
import {AssetService} from '../../service/asset.service';
import {AssetType, ContentType} from '../../model/asset/Asset';
import {NotificationHandler} from '../../helper/notification.handler';
import {DataHelper} from '../../helper/data.helper';
import {debounceTime} from 'rxjs/operators';
import {MathService} from '../../math/math.service';

export interface QuestionDetailsData {
  question?: Question;
  origQuestion?: Question;

  localizeMode?: boolean;
}

@Component({
  selector: 'app-contest-question-details-modal',
  templateUrl: './contest-question-details-modal.component.html',
  styleUrls: ['./contest-question-details-modal.component.scss']
})
export class ContestQuestionDetailsModalComponent extends BaseModalComponent implements OnInit {

  readonly SINGLE_CHOICE = QuestionType.single_choice;
  readonly MULTIPLE_CHOICE = QuestionType.multiple_choice;
  readonly roles = [Role.Host];

  @ViewChild('dialogContent', {static: true}) protected progressContainer!: ElementRef;
  @ViewChild('editorComponent', {static: true}) editor!: EditorComponent;

  question!: Question;
  origQuestion!: Question;

  localizeMode: boolean;

  imageFile?: File;
  audioFile?: File;
  videoFile?: File;

  questionField = new FormControl('', [
    Validators.required, Validators.minLength(1), Validators.maxLength(5120)
  ]);
  typeField = new FormControl('', [
    Validators.required
  ]);
  pointsField = new FormControl('', [
    Validators.required, Validators.min(1), Validators.max(127)
  ]);

  questionForm = new FormGroup({
    question: this.questionField,
    type: this.typeField,
    points: this.pointsField,
  });

  initEditor: boolean = false;
  editorConfig: object;

  constructor(private assetService: AssetService,
              private mathService: MathService,
              dialogRef: MatDialogRef<ContestQuestionDetailsModalComponent>,
              progressService: ProgressService,
              breakpointObserver: BreakpointObserver,
              @Inject(LOCALE_ID) locale: string,
              @Inject(MAT_DIALOG_DATA) data: QuestionDetailsData) {
    super(dialogRef, progressService);

    this.localizeMode = data ? data.localizeMode || false : false;

    if (data && data.question) {
      this.question = data.question;
      if (this.question) this.origQuestion = this.question.clone(true);
    } else {
      this.question = new Question();
    }
    this.question.answers.forEach(answer => this.addAnswer(answer));
    while (this.question.answers.length < 2) {
      this.addAnswer();
    }
    this.questionField.setValue(this.question.text);
    this.typeField.setValue(this.question.type);

    this.pointsField.setValue(this.question.points || 1);
    if (this.localizeMode) {
      this.pointsField.disable();
    } else {
      this.pointsField.valueChanges.pipe(debounceTime(300)).subscribe(value => {
        if (value != this.question.points) this.question.points = parseInt(value);
      });
    }

    const isMobile = breakpointObserver.isMatched(Breakpoints.HandsetPortrait);
    const descriptionPlaceholder = $localize`:Contest Tasks Editor|Contest Tasks question input placeholder@@contest-tasks-editor-question-input-placeholder:Question text...`;

    this.editorConfig = EditorConfigHelper.config(locale, descriptionPlaceholder, !isMobile, 200, true);
  }

  ngOnInit(): void {
    this.initEditor = true;
  }

  get editorApiKey() {
    return EditorConfigHelper.editorApiKey;
  }

  compareTypes(t1?: QuestionType, t2?: QuestionType): boolean {
    return t1 == t2;
  }

  loadTypes = (): Observable<QuestionType[]> => {
    return of([
      QuestionType.single_choice,
      QuestionType.multiple_choice
    ]);
  };

  displayType(type: QuestionType): string {
    return localizeQuestionType(type);
  }

  get hasCorrectAnswer(): boolean {
    return this.question.answers
      .map(a => a.correct)
      .reduce((a1, a2) => a1 || a2) || false;
  }

  get hasSameAnswer(): boolean {
    const answers = new Set<string>();

    for (const answer of this.question.answers) {
      if (answer.text) {
        if (answers.has(answer.text)) {
          return true;
        } else {
          answers.add(answer.text);
        }
      }
    }

    return false;
  }

  private renderMath(): void {
    setTimeout(() => {
      this.mathService.renderPageMath();
    }, 1);
  }

  addAnswer(answer?: Answer) {
    const a = answer || new Answer();

    if (!a.id) a.id = DataHelper.generateId();

    const formControl = new FormControl('', [
      Validators.required, Validators.minLength(1), Validators.maxLength(256)
    ]);
    formControl.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      if (a.text != value) {
        a.text = value as string;
        this.renderMath();
      }
    });
    if (answer) formControl.setValue(answer.text);
    this.questionForm.addControl(a.id, formControl);

    if (!answer) this.question.answers.push(a);
  }

  getAnswerControl(answer: Answer): FormControl {
    return this.questionForm.controls[answer.id || ''] as FormControl;
  }

  proceedAllowed(): boolean {
    return this.questionForm.valid && this.question.answers.length >= 2 && this.hasCorrectAnswer && !this.hasSameAnswer;
  }

  async proceed(): Promise<void> {
    this.startProgress();

    this.question.text = this.questionField.value as string;
    this.question.type = this.typeField.value as QuestionType;
    this.question.points = parseInt(this.pointsField.value);

    this.question.answers.forEach((answer, index) => {
      answer.text = this.getAnswerControl(answer).value as string;
      // if (DataHelper.isAutoGeneratedId(answer.id)) answer.id = undefined;
      answer.index = index + 1;
    });

    const data: QuestionDetailsData = {
      question: this.question,
    };

    if (this.imageFile || this.audioFile || this.videoFile) {
      try {
        if (this.imageFile) this.question.image = await this.uploadAsset(this.imageFile, AssetType.question_image);
        if (this.audioFile) this.question.audio = await this.uploadAsset(this.audioFile, AssetType.question_audio);
        if (this.videoFile) this.question.video = await this.uploadAsset(this.videoFile, AssetType.question_video);

        this.closeModal(data);
      } catch (error) {
        this.stopProgress();
      }
    } else {
      this.closeModal(data);
    }
  }

  private uploadAsset(file: File, type: AssetType): Promise<string | undefined> {
    return new Promise<string | undefined>((resolve, reject) => {
      this.assetService.registerAssetUpload(type, file).subscribe(asset => {
        if (asset.uploadUrl && file) this.assetService.uploadContent(asset.uploadUrl, file).subscribe(() => {
          resolve(asset.url);
        }, error => {
          NotificationHandler.handleError($localize`:Notifications|Asset upload failed message@@notifications-asset-upload-failed-error:Asset upload has failed`, error);

          reject(error);
        });
      }, error => {
        NotificationHandler.handleError($localize`:Notifications|Asset upload registration failed message@@notifications-asset-upload-registration-failed-error:Asset upload registration has failed`, error);

        reject(error);
      });
    });
  }

  setSingleAnswerCorrect(answer: Answer) {
    if (answer) this.question.answers.forEach(a => a.correct = a == answer);
  }

  setAnswerCorrect(selectionChange: MatSelectionListChange) {
    this.question.answers
      .filter(a => selectionChange.options.findIndex(option => option.value == a) >= 0)
      .forEach(a => a.correct = selectionChange.options.find(option => option.value == a)?.selected || false);
  }

  handleAnswerDelete(answer: Answer) {
    const index = this.question.answers.findIndex(a => a.id == answer.id);
    if (index >= 0) this.question.answers.splice(index, 1);
  }

  onImageFileSelected(files: File[]) {
    this.imageFile = files && files.length > 0 ? files[0] : undefined;
  }

  onAudioFileSelected(files: File[]) {
    this.audioFile = files && files.length > 0 ? files[0] : undefined;
  }

  onVideoFileSelected(files: File[]) {
    this.videoFile = files && files.length > 0 ? files[0] : undefined;
  }

  cancelUpdate() {
    const data: QuestionDetailsData = {};
    if (this.origQuestion) data.origQuestion = this.origQuestion;

    this.closeModal(data);
  }

  handleQuestionTypeChange(type: QuestionType) {
    this.typeField.setValue(type);

    if (type == QuestionType.single_choice && this.question.answers.filter(answer => answer.correct).length > 1) {
      this.question.answers.forEach(answer => answer.correct = false);
    }
  }
}
